import type { FilterRuleModel, OptimizationIDModel } from '@/shared/generated'

import React, { useEffect } from 'react'
import { motion } from 'framer-motion'
import { SearchIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { SalesFactorsImpactWidget } from '@/widgets/sales-factors-impact'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/shared/components/ui/accordion'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
} from '@/shared/components/ui/empty'
import { ampli } from '@/shared/lib/ampli'
import { cn } from '@/shared/lib/utils'

import { BusinessMetricsTable } from './BusinessMetricTable'
import OptimizationMetricsWidget from './OptimizationMetricsGlobalWidget'

export const OptimizationTab = (props: {
	queryParams: {
		optimizations: OptimizationIDModel[]
		filters: FilterRuleModel[]
	} | null
	isLoading: boolean
}) => {
	const { t } = useTranslation()

	useEffect(() => {
		ampli.optSummaryGlobOpenOptimizationTab()
	}, [])

	return (
		<div className='divide-base flex w-full flex-col divide-y divide-solid px-4 pt-4 pb-8'>
			<ErrorBoundary>
				<Accordion
					defaultValue={['forecast', 'salesFactorsImpact']}
					type='multiple'
					className='w-full'
				>
					<AccordionItem value='forecast'>
						<AccordionTrigger>
							{t('opt.summary.metrics_forecast', {
								defaultValue: 'Metrics forecast',
							})}
						</AccordionTrigger>
						<AccordionContent>
							<div className='space-y-3'>
								<OptimizationMetricsWidget {...props} />
								<BusinessMetricsTable {...props} />
							</div>
						</AccordionContent>
					</AccordionItem>
					<AccordionItem value='distribution'>
						<AccordionTrigger>
							<div className='grid gap-1'>
								<h5 className='text-base'>
									{t('elasticity.distribution', {
										defaultValue: 'Distribution of elasticity',
									})}
								</h5>
								<p className='text-sm font-normal text-muted-foreground'>
									{t('elasticity.distribution.subtitle', {
										defaultValue:
											'Click on tab to see different additional insights.',
									})}
								</p>
							</div>
						</AccordionTrigger>
						<AccordionContent>
							<NoData />
						</AccordionContent>
					</AccordionItem>
					<AccordionItem value='salesFactorsImpact'>
						<AccordionTrigger>
							<div className='grid gap-1'>
								<h5 className='text-base'>
									{t('widget.top_sales_factors.title', {
										defaultValue: 'Top sales factors',
									})}
								</h5>
								<p className='text-sm font-normal text-muted-foreground'>
									{t('widget.top_sales_factors.subtitle', {
										defaultValue:
											'This chart shows how different factors affect your sales in available key metrics.',
									})}
								</p>
							</div>
						</AccordionTrigger>
						<AccordionContent>
							<SalesFactorsImpactWidget
								filters={props.queryParams?.filters ?? []}
								isLoading={props.isLoading}
							/>
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</ErrorBoundary>
		</div>
	)
}

const NoData = () => {
	const { t } = useTranslation()

	return (
		<div className='flex h-40 w-full items-center justify-center rounded-lg px-4'>
			<Empty>
				<EmptyHeader>
					<EmptyMedia variant='icon'>
						<SearchIcon />
					</EmptyMedia>
					<EmptyDescription>
						{t('no_data', { defaultValue: 'Looks like there`s no data.' })}
					</EmptyDescription>
				</EmptyHeader>
			</Empty>
		</div>
	)
}

export const TabContent = ({
	children,
	className,
}: {
	children: React.ReactNode
	className?: string
}) => (
	<motion.div
		initial={{ opacity: 0, x: 100 }}
		animate={{ opacity: 1, x: 0 }}
		exit={{ opacity: 0, x: -30 }}
		className={cn(
			'divide-base flex size-full flex-col divide-y divide-solid',
			className
		)}
		transition={{
			duration: 0.2,
		}}
	>
		{children}
	</motion.div>
)
